<template>
	<InvoiceCreatorLayout
		v-if="form"
		ref="layoutRef"
		class="company-record-editor document-editor--refreshed"
		:title="pageTitle"
	>
		<template #status>
			<span class="version-badge">{{ formatRecordVersion(record.version) }}</span>
			<span v-if="!editable" class="badge badge-muted">{{ lockReason === 'deleted' ? 'Deleted' : 'Archived' }}</span>
			<span v-else class="save-state" :class="`save-state--${saveState}`" role="status">{{ saveStateLabel }}</span>
		</template>

		<template #details>
			<section class="document-details-panel">
				<div v-if="notice" class="record-notice" :class="`record-notice--${notice.tone}`" role="status">
					<span>{{ notice.text }}</span>
					<button v-if="notice.action" type="button" class="notice-action" @click="notice.action.run">
						{{ notice.action.label }}
					</button>
				</div>

				<div class="editor-details-grid">
					<div class="form-group">
						<span>Document reference</span>
						<input class="field-input disabled-input" type="text" :value="record.reference" disabled />
					</div>
					<div class="form-group">
						<span>Version</span>
						<input class="field-input disabled-input" type="text" :value="formatRecordVersion(record.version)" disabled />
					</div>
					<div class="form-group">
						<span>Reviewed</span>
						<DateRangePicker
							mode="month"
							use-fixed
							:date-from="form.reviewedOn"
							:disabled="!editable"
							trigger-label="Reviewed"
							placeholder="Select month"
							@update="({ dateFrom }) => setReviewedOn(dateFrom)"
						>
							<template #trigger-icon><AppointmentIcon name="calendar" :size="14" /></template>
						</DateRangePicker>
					</div>
					<div class="form-group">
						<span>Next review</span>
						<DateRangePicker
							mode="month"
							use-fixed
							:date-from="form.nextReviewOn"
							:disabled="!editable"
							trigger-label="Next review"
							placeholder="Select month"
							@update="({ dateFrom }) => (form.nextReviewOn = dateFrom)"
						>
							<template #trigger-icon><AppointmentIcon name="calendar" :size="14" /></template>
						</DateRangePicker>
					</div>
				</div>

				<ul class="record-intro">
					<li v-for="line in form.content.intro" :key="line">{{ line }}</li>
				</ul>

				<p v-if="editable && (dueSummary.overdue || dueSummary.soon)" class="due-summary">
					<span v-if="dueSummary.overdue" class="badge badge-danger">{{ dueSummary.overdue }} overdue</span>
					<span v-if="dueSummary.soon" class="badge badge-warning">{{ dueSummary.soon }} due within {{ DUE_SOON_DAYS }} days</span>
				</p>
			</section>
		</template>

		<template #items>
			<div class="record-form">
				<section class="editor-section">
					<div class="section-heading"><h2>Review completed</h2></div>
					<div class="document-table-scroll">
						<table class="editor-table review-table">
							<colgroup>
								<col class="review-year-col" />
								<col />
								<col class="date-col" />
							</colgroup>
							<thead>
								<tr>
									<th>Year</th>
									<th>Completed by</th>
									<th>Date</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="entry in earlierReviews" :key="entry.key" class="is-earlier">
									<td class="preset-cell">{{ entry.year }}</td>
									<td class="preset-cell">{{ entry.completedBy || '—' }}</td>
									<td class="preset-cell">{{ entry.date || '—' }}</td>
								</tr>
								<tr>
									<td class="preset-cell">{{ reviewYear }}</td>
									<td>
										<input
											v-model="form.content.signOff.completedBy"
											class="table-input"
											type="text"
											placeholder="Name"
											aria-label="Review completed by"
											:readonly="!editable"
										/>
									</td>
									<td class="date-cell">
										<DateRangePicker
											mode="single"
											manual-input
											manual-input-placeholder=""
											use-fixed
											:date-from="form.content.signOff.date"
											:disabled="!editable"
											placeholder="Date"
											@update="({ dateFrom }) => (form.content.signOff.date = dateFrom)"
										/>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>

				<section v-for="section in form.content.sections" :key="section.key" class="editor-section">
					<div class="section-heading"><h2>{{ section.title }}</h2></div>
					<p v-if="section.intro" class="section-intro">{{ section.intro }}</p>
					<div class="document-table-scroll">
						<table class="editor-table checklist-table">
							<colgroup>
								<col class="item-col" />
								<col />
								<col class="often-col" />
								<col class="who-col" />
								<col class="date-col" />
								<col class="date-col" />
								<col class="notes-col" />
							</colgroup>
							<thead>
								<tr>
									<th>Item</th>
									<th>What to check</th>
									<th>How often</th>
									<th>Who</th>
									<th>Next due</th>
									<th>Done</th>
									<th>Notes</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="row in section.rows" :key="row.key">
									<td class="preset-cell item-cell">{{ row.item }}</td>
									<td class="preset-cell">{{ row.check }}</td>
									<td class="preset-cell">{{ row.frequency }}</td>
									<td class="preset-cell">{{ row.who }}</td>
									<td class="date-cell" :class="dueCellClass(row.key)" v-tooltip="() => dueTooltip(row.key)">
										<DateRangePicker
											mode="single"
											manual-input
											manual-input-placeholder=""
											use-fixed
											:date-from="form.content.entries[row.key].nextDue"
											:disabled="!editable"
											placeholder="—"
											@update="({ dateFrom }) => (form.content.entries[row.key].nextDue = dateFrom)"
										/>
									</td>
									<td class="date-cell">
										<DateRangePicker
											mode="single"
											manual-input
											manual-input-placeholder=""
											use-fixed
											:date-from="form.content.entries[row.key].done"
											:disabled="!editable"
											placeholder="—"
											@update="({ dateFrom }) => (form.content.entries[row.key].done = dateFrom)"
										/>
									</td>
									<td class="notes-cell">
										<textarea
											v-model="form.content.entries[row.key].notes"
											v-textarea-autosize
											class="table-textarea"
											rows="1"
											:aria-label="`Notes: ${row.item}`"
											:readonly="!editable"
										></textarea>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>

				<div class="toolbar-bottom-spacer" aria-hidden="true"></div>
			</div>
		</template>

		<template #footer>
			<button class="floating-action-btn" type="button" @click="openPreview">
				<AppointmentIcon name="preview" :size="18" />
				<span>Preview</span>
			</button>
			<button class="floating-action-btn" type="button" :disabled="pdfGenerating" @click="downloadPdf">
				<AppointmentIcon name="download" :size="18" />
				<span>{{ pdfGenerating ? 'Generating...' : 'Download PDF' }}</span>
			</button>
			<button v-if="editable" class="floating-action-btn" type="button" @click="askStartNextReview">
				<AppointmentIcon name="plus" :size="18" />
				<span>Start next review</span>
			</button>
		</template>
	</InvoiceCreatorLayout>
	<div v-else class="loading-state">{{ loadError || 'Loading record...' }}</div>

	<CompanyRecordDialog
		v-if="dialog"
		:dialog="dialog"
		:busy="startingNext"
		@cancel="dialog = null"
		@confirm="startNextReview"
	/>

	<InvoicePreview
		:show-preview="showPreview"
		:invoice-html="previewHtml"
		render-html-as-pdf
		:pdf-filename="pdfFilename"
		@close-preview="showPreview = false"
		@download-pdf="downloadPdf"
	/>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref, watch } from 'vue';
import { addYears, format, isValid, parseISO } from 'date-fns';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import InvoicePreview from '@/components/invoices/InvoicePreview.vue';
import CompanyRecordDialog from '@/components/company-records/CompanyRecordDialog.vue';
import InvoiceCreatorLayout from '@/views/invoices/components/InvoiceCreatorLayout.vue';
import vTooltip from '@/directives/tooltip';
import { vTextareaAutosize } from '@/directives/textareaAutosize';
import { useLiveRefresh } from '@/composables/useLiveRefresh';
import { companyRecordErrorCode, useCompanyRecordsStore } from '@/stores/companyRecords';
import {
	createNextVersion,
	DUE_SOON_DAYS,
	entryDueState,
	formatRecordDate,
	formatRecordMonth,
	formatRecordVersion,
	getCompanyRecordTemplate,
	isCurrentVersion,
	mergeRecordContent,
	nextReviewDialog,
	sameContent,
	summarizeRecord,
	todayIso,
} from '@/utils/companyRecords';
import { deriveCompanyRecordFilename, generateCompanyRecordHtml } from '@/utils/companyRecordDocument';
import { downloadProposalPdfFromHtml } from '@/utils/proposalDocument';

const props = defineProps({
	documentId: { type: String, required: true },
	tabId: { type: String, default: null },
});

const emit = defineEmits(['open-record']);

const store = useCompanyRecordsStore();
const { proxy } = getCurrentInstance() || {};
const toast = proxy?.$toast;
const logError = proxy?.$logError;

// Changes are saved this long after typing stops.
const SAVE_DELAY_MS = 800;
const RETRY_DELAY_MS = 5000;

const record = ref(null);
const form = ref(null);
// What the server holds as far as this device knows; edits are compared and merged against it.
const base = ref(null);
const revision = ref(0);
const loadError = ref('');
const saving = ref(false);
const saveFailed = ref(false);
const lockReason = ref('');
const showPreview = ref(false);
const pdfGenerating = ref(false);
const dialog = ref(null);
const startingNext = ref(false);
const layoutRef = ref(null);
let saveTimer = null;
let savedScrollTop = null;

const clone = (value) => JSON.parse(JSON.stringify(value));
const editableFields = (source) => ({
	reviewedOn: source.reviewedOn || '',
	nextReviewOn: source.nextReviewOn || '',
	content: clone(source.content),
});

const template = computed(() => getCompanyRecordTemplate(record.value?.templateKey));
const pageTitle = computed(() => record.value?.title || template.value?.title || 'Company record');
const versions = computed(() =>
	store.items.filter(
		(item) =>
			item.templateKey === record.value?.templateKey &&
			(item.subject?.documentId || '') === (record.value?.subject?.documentId || ''),
	),
);
const newerVersion = computed(() =>
	versions.value
		.filter((item) => item.version > (record.value?.version || 0))
		.sort((a, b) => b.version - a.version)[0] || null,
);
const editable = computed(() =>
	Boolean(record.value) && !lockReason.value && isCurrentVersion(record.value, store.items),
);
const isDirty = computed(() => Boolean(form.value && base.value) && !sameContent(form.value, base.value));

const saveState = computed(() => {
	if (saving.value) return 'saving';
	if (saveFailed.value) return 'error';
	if (isDirty.value) return 'dirty';
	return 'saved';
});
const saveStateLabel = computed(
	() => ({ saving: 'Saving...', error: 'Not saved — retrying', dirty: 'Unsaved changes', saved: 'Saved' })[saveState.value],
);

const notice = computed(() => {
	if (lockReason.value === 'deleted') {
		return { tone: 'danger', text: 'This version was deleted. Changes to it are no longer saved.' };
	}
	if (!editable.value && record.value) {
		const current = newerVersion.value;
		return {
			tone: 'muted',
			text: `Archived version, kept as it was reviewed. It can no longer be changed.`,
			action: current
				? { label: `Open ${formatRecordVersion(current.version)}`, run: () => emit('open-record', { record: current }) }
				: null,
		};
	}
	if (summarizeRecord({ ...record.value, ...form.value }).reviewDue) {
		return {
			tone: 'warning',
			text: `The yearly review was due in ${formatRecordMonth(form.value.nextReviewOn)}.`,
			action: { label: 'Start next review', run: () => askStartNextReview() },
		};
	}
	return null;
});

const today = ref(todayIso());
const dueSummary = computed(() => summarizeRecord({ ...record.value, ...form.value }, today.value));
const dueCellClass = (key) => `due-${entryDueState(form.value.content.entries[key], today.value)}`;
const dueTooltip = (key) =>
	({ overdue: 'Overdue', soon: `Due within ${DUE_SOON_DAYS} days`, done: 'Done for this due date' })[
		entryDueState(form.value.content.entries[key], today.value)
	] || '';

const reviewYear = computed(() => String(form.value?.reviewedOn || '').slice(0, 4));
const earlierReviews = computed(() =>
	versions.value
		.filter((item) => item.version < (record.value?.version || 0))
		.sort((a, b) => a.version - b.version)
		.map((item) => ({
			key: item.documentId,
			year: String(item.reviewedOn || '').slice(0, 4),
			completedBy: item.content.signOff.completedBy,
			date: formatRecordDate(item.content.signOff.date),
		})),
);

const parseDate = (value) => {
	const date = value ? parseISO(value) : null;
	return date && isValid(date) ? date : null;
};

// The next review follows the review month a year on, unless it was set to something else.
const setReviewedOn = (value) => {
	const previous = parseDate(form.value.reviewedOn);
	const followed = !form.value.nextReviewOn ||
		(previous && format(addYears(previous, 1), 'yyyy-MM-dd') === form.value.nextReviewOn);
	form.value.reviewedOn = value;
	const next = parseDate(value);
	if (followed && next) form.value.nextReviewOn = format(addYears(next, 1), 'yyyy-MM-dd');
};

const applyServerRecord = (saved) => {
	record.value = saved;
	revision.value = saved.revision;
	base.value = editableFields(saved);
};

const show = (loaded) => {
	applyServerRecord(loaded);
	form.value = editableFields(loaded);
};

// The list's copy shows at once; the saved record replaces it unless editing has begun.
const load = async () => {
	loadError.value = '';
	const cached = store.items.find((item) => item.documentId === props.documentId);
	if (cached) show(cached);
	if (!store.hasFetched) void store.fetchRecords().catch((error) => logError?.(error, 'CompanyRecordEditorView versions'));
	try {
		const latest = await store.fetchRecord(props.documentId);
		if (latest && (!form.value || (!isDirty.value && latest.revision > revision.value))) show(latest);
	} catch (error) {
		logError?.(error, 'CompanyRecordEditorView load');
		if (error?.response?.status === 404) {
			if (cached) lockReason.value = 'deleted';
			else loadError.value = 'This record no longer exists.';
		} else if (!cached) {
			loadError.value = 'Failed to load the record.';
		}
	}
};

const clearSaveTimer = () => {
	clearTimeout(saveTimer);
	saveTimer = null;
};

const scheduleSave = (delay = SAVE_DELAY_MS) => {
	clearSaveTimer();
	saveTimer = setTimeout(() => void save(), delay);
};

// The record moved on elsewhere: what changed here is laid over what is saved now, then saved.
const mergeWithLatest = async () => {
	const latest = await store.fetchRecord(props.documentId);
	if (!latest) {
		lockReason.value = 'deleted';
		return;
	}
	const merged = mergeRecordContent(base.value, form.value, editableFields(latest));
	applyServerRecord(latest);
	form.value = merged;
};

const save = async () => {
	clearSaveTimer();
	if (saving.value || !editable.value || !isDirty.value) return;
	saving.value = true;
	const sent = clone(form.value);
	try {
		const saved = await store.updateRecord(props.documentId, { ...sent, revision: revision.value });
		record.value = saved;
		revision.value = saved.revision;
		base.value = sent;
		saveFailed.value = false;
	} catch (error) {
		const code = companyRecordErrorCode(error);
		if (code === 'COMPANY_RECORD_CHANGED') {
			try {
				await mergeWithLatest();
			} catch (mergeError) {
				logError?.(mergeError, 'CompanyRecordEditorView merge');
				saveFailed.value = true;
			}
		} else if (code === 'COMPANY_RECORD_ARCHIVED') {
			lockReason.value = 'archived';
			toast?.error?.('A newer version of this record was started on another device. Your last change was not saved.');
			void store.fetchRecords({ force: true }).catch(() => {});
		} else if (error?.response?.status === 404) {
			lockReason.value = 'deleted';
		} else {
			logError?.(error, 'CompanyRecordEditorView save');
			saveFailed.value = true;
		}
	} finally {
		saving.value = false;
	}
	if (editable.value && isDirty.value) scheduleSave(saveFailed.value ? RETRY_DELAY_MS : 0);
};

// Deleted elsewhere: the list no longer has this version once it is read again.
watch(
	() => store.items,
	(items) => {
		if (!record.value || !store.hasFetched || store.unavailable) return;
		if (!items.some((item) => item.documentId === record.value.documentId)) lockReason.value = 'deleted';
	},
);

watch(
	form,
	() => {
		if (editable.value && isDirty.value && !saving.value) scheduleSave();
	},
	{ deep: true },
);

// Leaving the tab or the app saves straight away rather than after the pause.
const flush = () => {
	if (saveTimer) void save();
};
const handleVisibility = () => {
	if (document.visibilityState === 'hidden') flush();
};

// Tabs share one scrolling area: a record opens at its top and keeps its place between visits.
const scrollArea = () => layoutRef.value?.$el?.closest?.('.tab-content') || null;

onMounted(async () => {
	document.addEventListener('visibilitychange', handleVisibility);
	await load();
	await nextTick();
	scrollArea()?.scrollTo?.({ top: 0 });
});
onActivated(async () => {
	today.value = todayIso();
	if (savedScrollTop === null) return;
	await nextTick();
	scrollArea()?.scrollTo?.({ top: savedScrollTop });
});
onDeactivated(() => {
	savedScrollTop = scrollArea()?.scrollTop ?? null;
	flush();
});
onBeforeUnmount(() => {
	document.removeEventListener('visibilitychange', handleVisibility);
	flush();
});

// Changes made on another device show up while nothing here is being edited.
const isEditorBusy = () =>
	!form.value || saving.value || isDirty.value || Boolean(saveTimer) || showPreview.value || Boolean(dialog.value);

useLiveRefresh(
	async ({ canApply }) => {
		if (isEditorBusy()) return;
		const since = store.changeCount;
		const items = await store.peekRecords();
		if (!canApply() || isEditorBusy() || !store.applyRecords(items, { since })) return;
		const latest = items.find((item) => item.documentId === props.documentId);
		// Revisions only grow, so an older read never replaces what was saved since.
		if (latest && latest.revision > revision.value) show(latest);
	},
	{ isBusy: isEditorBusy },
);

const pdfRecord = () => ({ ...record.value, ...clone(form.value) });
const pdfHistory = () => versions.value.filter((item) => item.version < record.value.version);
const pdfFilename = computed(() => (record.value ? deriveCompanyRecordFilename(record.value) : 'company-record.pdf'));
const previewHtml = computed(() =>
	showPreview.value ? generateCompanyRecordHtml({ record: pdfRecord(), history: pdfHistory() }) : '',
);

const openPreview = () => {
	showPreview.value = true;
};

const downloadPdf = async () => {
	if (pdfGenerating.value) return;
	pdfGenerating.value = true;
	try {
		await downloadProposalPdfFromHtml({
			html: generateCompanyRecordHtml({ record: pdfRecord(), history: pdfHistory() }),
			filename: pdfFilename.value,
			logError,
		});
	} catch (error) {
		logError?.(error, 'CompanyRecordEditorView downloadPdf');
		toast?.error?.('Failed to download PDF.');
	} finally {
		pdfGenerating.value = false;
	}
};

const askStartNextReview = () => {
	dialog.value = nextReviewDialog(record.value, pageTitle.value);
};

const startNextReview = async () => {
	if (startingNext.value) return;
	startingNext.value = true;
	try {
		if (isDirty.value) await save();
		const next = await store.createRecord(createNextVersion({ ...record.value, ...clone(form.value) }));
		dialog.value = null;
		emit('open-record', { record: next, replaceTabId: props.tabId });
	} catch (error) {
		logError?.(error, 'CompanyRecordEditorView startNextReview');
		toast?.error?.(error?.response?.data?.error?.message || 'Failed to start the next review.');
		dialog.value = null;
		void store.fetchRecords({ force: true }).catch(() => {});
	} finally {
		startingNext.value = false;
	}
};
</script>

<style scoped>
.company-record-editor {
	padding-bottom: 24px;
}

.version-badge {
	display: inline-flex;
	align-items: center;
	min-height: 22px;
	padding: 2px 8px;
	border-radius: 4px;
	background: var(--noc-blue-05);
	color: var(--noc-blue);
	font: 600 0.75rem/1rem var(--font-family-base);
}

.save-state {
	color: var(--text-color-secondary);
	font: 500 0.8125rem/1.125rem var(--font-family-base);
}

.save-state--dirty,
.save-state--error {
	color: #c05621;
}

.badge {
	display: inline-flex;
	align-items: center;
	min-height: 22px;
	padding: 2px 8px;
	border-radius: 4px;
	font: 600 0.75rem/1rem var(--font-family-base);
	white-space: nowrap;
}

.badge-warning {
	background: var(--report-status-warning-background);
	color: var(--report-status-warning-color);
}

.badge-danger {
	background: var(--report-status-danger-background);
	color: var(--report-status-danger-color);
}

.badge-muted {
	background: var(--report-status-muted-background);
	color: var(--report-status-muted-color);
}

.document-details-panel {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 16px;
	min-width: 0;
}

.record-notice {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px 16px;
	width: min(720px, 100%);
	box-sizing: border-box;
	padding: 10px 14px;
	border-radius: 6px;
	font: 500 0.875rem/1.25rem var(--font-family-base);
}

.record-notice--muted {
	background: var(--report-status-muted-background);
	color: var(--report-status-muted-color);
}

.record-notice--warning {
	background: var(--report-status-warning-background);
	color: var(--report-status-warning-color);
}

.record-notice--danger {
	background: var(--report-status-danger-background);
	color: var(--report-status-danger-color);
}

.notice-action {
	padding: 0;
	border: 0;
	background: transparent;
	color: var(--noc-blue);
	font: 600 0.875rem/1.25rem var(--font-family-base);
	cursor: pointer;
	touch-action: manipulation;
}

.record-intro {
	margin: 0;
	padding: 0;
	list-style: none;
	color: var(--text-color-secondary);
	font: 400 0.875rem/1.375rem var(--font-family-base);
}

.record-intro li::before {
	content: '–';
	margin-right: 10px;
}

.due-summary {
	display: flex;
	gap: 8px;
	margin: 0;
}

.record-form {
	display: flex;
	flex-direction: column;
	gap: 20px;
	min-width: 0;
}

.editor-section {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 8px;
	min-width: 0;
}

.editor-section + .editor-section {
	padding-top: 20px;
	border-top: 1px solid var(--border-color-secondary, #ebeef1);
}

.section-heading {
	display: flex;
	align-items: center;
	min-height: 34px;
}

.section-intro {
	max-width: 900px;
	margin: 0;
	color: var(--text-color-secondary);
	font: 400 0.875rem/1.375rem var(--font-family-base);
}

.document-table-scroll {
	width: 100%;
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;
}

.editor-table {
	width: 100%;
	border: 1px solid var(--border-color);
	border-collapse: collapse;
	table-layout: fixed;
}

.checklist-table {
	min-width: 960px;
}

.review-table {
	width: min(720px, 100%);
	min-width: 480px;
}

.editor-table th {
	height: 38px;
	padding: 8px 12px;
	border-bottom: 1px solid var(--border-color);
	border-right: 1px solid var(--border-color);
	background: var(--noc-background-secondary);
	color: var(--text-color);
	text-align: left;
	font: 600 13px/20px var(--font-family-base, sans-serif);
}

.editor-table td {
	height: 40px;
	padding: 0;
	border-bottom: 1px solid var(--border-color);
	border-right: 1px solid var(--border-color);
	background: var(--input-background, #fff);
	vertical-align: top;
}

.editor-table .preset-cell {
	padding: 10px 12px;
	background: var(--noc-background-secondary);
	color: var(--text-color);
	font: 400 14px/20px var(--font-family-base, sans-serif);
	overflow-wrap: anywhere;
}

.editor-table .item-cell {
	font-weight: 600;
}

.editor-table .is-earlier .preset-cell {
	color: var(--text-color-secondary);
}

.item-col {
	width: 14%;
}

.often-col {
	width: 10%;
}

.who-col {
	width: 112px;
}

.date-col {
	width: 128px;
}

.notes-col {
	width: 18%;
}

.review-year-col {
	width: 90px;
}

.document-editor--refreshed .editor-table :is(.table-input, .table-textarea) {
	display: block;
	width: 100%;
	min-width: 0;
	min-height: 40px;
	margin: 0;
	padding: 10px 12px;
	border: 0 !important;
	border-radius: 0;
	background: transparent;
	box-shadow: none;
	color: var(--text-color);
	font: 400 14px/20px var(--font-family-base, sans-serif);
	outline: none;
	resize: none;
	overflow: hidden;
}

.document-editor--refreshed .editor-table :is(.table-input, .table-textarea):is(:hover, :focus):not([readonly]) {
	background: var(--noc-background-secondary);
}

.date-cell :deep(.date-range-picker) {
	width: 100%;
}

.date-cell :deep(.drp-manual-control) {
	display: flex;
	align-items: stretch;
	width: 100%;
	min-width: 0;
	box-sizing: border-box;
	min-height: 40px;
	padding: 0;
	border: 0;
	border-radius: 0;
	background: transparent;
	box-shadow: none;
}

.date-cell :deep(.drp-manual-input) {
	flex: 1 1 0;
	width: 0;
	min-width: 0;
	min-height: 40px;
	padding: 10px 0 10px 12px;
	border: 0;
	background: transparent;
	font: 400 14px/20px var(--font-family-base, sans-serif);
	font-variant-numeric: tabular-nums;
	color: inherit;
	outline: none;
}

.date-cell :deep(.drp-manual-toggle) {
	flex: 0 0 34px;
	width: 34px;
	min-height: 40px;
	padding: 0;
	border: 0;
	background: transparent;
	color: var(--text-color-secondary);
}

.date-cell :deep(.drp-manual-control:is(:hover, :focus-within)) {
	background: var(--noc-background-secondary);
}

.date-cell :deep(.drp-manual-control--invalid) {
	box-shadow: inset 0 -1px var(--noc-red);
}

.date-cell.due-overdue {
	background: var(--report-status-danger-background);
	color: var(--report-status-danger-color);
}

.date-cell.due-soon {
	background: var(--report-status-warning-background);
	color: var(--report-status-warning-color);
}

.date-cell.due-done {
	color: var(--report-status-success-color);
}

.toolbar-bottom-spacer {
	flex: 0 0 96px;
	height: 96px;
	pointer-events: none;
}

/* The floating toolbar sits at the bottom of the editor and follows the page, whatever the menu. */
:deep(.creator-footer) {
	position: sticky;
	z-index: 90;
	bottom: 22px;
	align-self: flex-start;
	display: flex;
	align-items: center;
	gap: 20px;
	width: max-content;
	max-width: 100%;
	margin: 0;
	padding: 12px 18px;
	box-sizing: border-box;
	border: 1px solid rgba(255, 255, 255, 0.58);
	border-radius: 12px;
	background: linear-gradient(180deg, rgba(255, 255, 255, 0.74), rgba(255, 255, 255, 0.54));
	box-shadow:
		0 18px 42px rgba(15, 23, 42, 0.13),
		0 2px 8px rgba(15, 23, 42, 0.06),
		inset 0 1px 0 rgba(255, 255, 255, 0.72);
	backdrop-filter: blur(22px) saturate(1.35);
	-webkit-backdrop-filter: blur(22px) saturate(1.35);
}

.floating-action-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 7px;
	height: 32px;
	min-height: 32px;
	padding: 4px 6px;
	border: 0;
	border-radius: 6px;
	background: transparent;
	color: var(--noc-blue, #0065ff);
	font-weight: 600;
	font-size: 0.86rem;
	white-space: nowrap;
	cursor: pointer;
	touch-action: manipulation;
}

.floating-action-btn:disabled {
	opacity: 0.5;
	cursor: default;
}

.loading-state {
	padding: 24px;
	text-align: center;
	color: var(--text-color-secondary);
}

@media (hover: hover) {
	.floating-action-btn:hover:not(:disabled) {
		background: var(--noc-background-secondary);
	}
}

@media (max-width: 640px) {
	:deep(.creator-footer) {
		gap: 8px;
		flex-wrap: wrap;
		justify-content: center;
		width: 100%;
	}
}
</style>

<style scoped src="@/views/client-documents/document-editor.css"></style>
